import React from 'react';
import { Link } from 'react-router-dom';
import { HelpCircle, Hourglass, User } from 'lucide-react';
import { ThemeToggle } from '../ui/ThemeToggle';
import { StreamerModeToggle } from '../ui/StreamerModeToggle';
import { OverallStreakCounter } from '../ui/OverallStreakCounter';
import { MobileMenu } from './MobileMenu';
import { GameStopwatch } from '../game/GameStopwatch';
import { cn } from '../../utils/cn';
import { useResetCountdown } from '../../hooks/useResetCountdown';
import { useCategoryTimer } from '../../hooks/useCategoryTimer';
import { StockCategoryFilter } from '../../types/stockCategories';

interface HeaderProps {
  onHelpClick: () => void;
  onStatsClick: () => void;
  onLeaderboardClick: () => void;
  onUserClick: () => void;
  onPremiumClick: () => void;
  onDiscussionClick: () => void;
  isUserLoggedIn?: boolean;
  isGameComplete?: boolean;
  category?: StockCategoryFilter;
  gameDate?: string;
  hideStreakAndTimer?: boolean;
  className?: string;
  /** Whether the game has started (user clicked Play on the start overlay). Defaults to true for backward compatibility. */
  hasGameStarted?: boolean;
  /** When true, show minimal mobile menu (hide game-specific items) */
  minimal?: boolean;
}

export const Header: React.FC<HeaderProps> = ({
  onHelpClick,
  onStatsClick,
  onLeaderboardClick,
  onUserClick,
  onPremiumClick,
  onDiscussionClick,
  isUserLoggedIn = false,
  isGameComplete = false,
  category,
  gameDate,
  hideStreakAndTimer = false,
  className,
  hasGameStarted = true,
  minimal = false
}) => {
  const countdown = useResetCountdown();
  const resetAccent = 'text-sky-700 dark:text-sky-300';

  // Always call timer hook to follow Rules of Hooks, but disable if category/gameDate not provided
  // Timer only starts after the user clicks Play on the start overlay (hasGameStarted)
  const timer = useCategoryTimer({
    category: category || 'popular', // Provide default category
    gameDate: gameDate || new Date().toISOString().split('T')[0], // Provide default date
    enabled: Boolean(category && gameDate && !isGameComplete && hasGameStarted) // Only enable when game has started
  });

  const showStopwatch = Boolean(!hideStreakAndTimer && category && gameDate && !isGameComplete && (timer.isCountingDown || timer.isRunning));
  const showDailyReset = !hideStreakAndTimer && isGameComplete;

  return (
    <header
      className={cn(
        "w-full bg-background",
        "sticky top-0 z-50",
        "relative",
        className
      )}
      role="banner"
    >
      <div className="mx-auto w-full px-4 py-1">
        <div className="grid grid-cols-[auto_auto_1fr] items-center gap-3 sm:grid-cols-[1fr_auto_1fr] sm:gap-4">
          {/* Left: App Title */}
          <div className="flex items-center justify-self-start gap-1.5">
            <Link
              to="/"
              className="flex items-center hover:opacity-80 transition-opacity gap-1.5"
              aria-label="Go to home page"
            >
              <div className="flex items-center justify-center rounded-lg bg-background dark:bg-background/80 w-6 h-6">
                <img
                  src="/assets/site-logo/cropped-neutral-logo.svg"
                  alt="Wallstreetle logo"
                  className="w-5 h-5 dark:hidden"
                />
                <img
                  src="/assets/site-logo/mini-transparent-neutral-logo.svg"
                  alt="Wallstreetle logo"
                  className="hidden w-5 h-5 dark:block"
                />
              </div>
              <h1 className="font-serif text-foreground text-lg">
                WALLSTREETLE
              </h1>
            </Link>
          </div>

          {/* Center: Streak + Timer Display */}
          <div className="hidden lg:flex items-center justify-center gap-3">
            {!hideStreakAndTimer && <OverallStreakCounter />}

            {/* Stopwatch during gameplay */}
            {showStopwatch && (
              <GameStopwatch
                isCountingDown={timer.isCountingDown}
                countdownSeconds={timer.countdownSeconds}
                isRunning={timer.isRunning}
                liveElapsedMs={timer.liveElapsedMs}
                className="justify-center"
              />
            )}

            {/* Daily reset after game complete */}
            {showDailyReset && (
              <div
                className="inline-flex items-center gap-1 text-foreground font-medium tracking-tight text-xs"
                role="status"
                aria-live="polite"
                aria-label={`Daily reset in ${countdown.formatted}`}
              >
                <Hourglass
                  className={cn('w-3 h-3', resetAccent)}
                  strokeWidth={2.4}
                  aria-hidden="true"
                />
                <span className={cn('font-normal', resetAccent)}>
                  Daily Reset:
                </span>
                <span className="font-bold tabular-nums">{countdown.formatted}</span>
              </div>
            )}
          </div>

          {/* Right: Navigation Buttons */}
          <nav
            className="flex items-center justify-end gap-2 col-start-3"
            role="navigation"
            aria-label="Main navigation"
          >
            {/* Mobile: Timer + Streak Counter + Hamburger Menu (< lg breakpoint) */}
            <div className="flex items-center gap-2 lg:hidden">
              {/* Stopwatch during gameplay */}
              {showStopwatch && (
                <GameStopwatch
                  isCountingDown={timer.isCountingDown}
                  countdownSeconds={timer.countdownSeconds}
                  isRunning={timer.isRunning}
                  liveElapsedMs={timer.liveElapsedMs}
                  className="justify-center text-sm"
                />
              )}

              {/* Daily reset after game complete */}
              {showDailyReset && (
                <div className="flex items-center gap-1 text-sm text-foreground">
                  <Hourglass
                    className={cn('w-3.5 h-3.5', resetAccent)}
                    strokeWidth={2.4}
                    aria-hidden="true"
                  />
                  <span className="font-semibold tabular-nums">{countdown.formatted}</span>
                </div>
              )}

              {!hideStreakAndTimer && <OverallStreakCounter />}
              <MobileMenu
                onHelpClick={onHelpClick}
                onStatsClick={onStatsClick}
                onLeaderboardClick={onLeaderboardClick}
                onUserClick={onUserClick}
                onPremiumClick={onPremiumClick}
                onDiscussionClick={onDiscussionClick}
                isUserLoggedIn={isUserLoggedIn}
                minimal={minimal}
              />
            </div>

            {/* Desktop Navigation (>= lg breakpoint) */}
            <div className="hidden lg:flex items-center gap-2">
              <ThemeToggle />
              <StreamerModeToggle />

              <button
                onClick={onHelpClick}
                className={cn(
                  "inline-flex items-center justify-center",
                  "w-6 h-6 rounded-full",
                  "transition-all duration-200 ease-in-out",
                  "focus:outline-none focus:ring-2 focus:ring-offset-2",
                  "hover:scale-105 active:scale-95",
                  "bg-gray-200 hover:bg-gray-300",
                  "focus:ring-gray-400 focus:ring-offset-white",
                  "dark:bg-gray-700 dark:hover:bg-gray-600",
                  "dark:focus:ring-gray-500 dark:focus:ring-offset-gray-900"
                )}
                aria-label="Open help modal"
                title="Help"
              >
                <HelpCircle className="w-3 h-3 text-gray-700 dark:text-gray-300" aria-hidden="true" />
              </button>

              {isUserLoggedIn ? (
                <button
                  onClick={onUserClick}
                  className={cn(
                    "inline-flex items-center justify-center",
                    "w-6 h-6 rounded-full",
                    "transition-all duration-200 ease-in-out",
                    "focus:outline-none focus:ring-2 focus:ring-offset-2",
                    "hover:scale-105 active:scale-95",
                    "bg-gray-200 hover:bg-gray-300",
                    "focus:ring-gray-400 focus:ring-offset-white",
                    "dark:bg-gray-700 dark:hover:bg-gray-600",
                    "dark:focus:ring-gray-500 dark:focus:ring-offset-gray-900"
                  )}
                  aria-label="Open user profile"
                  title="User Profile"
                >
                  <User className="w-3 h-3 text-gray-700 dark:text-gray-300" aria-hidden="true" />
                </button>
              ) : (
                <button
                  onClick={onUserClick}
                  className={cn(
                    "inline-flex items-center justify-center",
                    "h-6 px-2 rounded-lg",
                    "border border-black dark:border-white",
                    "transition-all duration-200 ease-in-out",
                    "focus:outline-none focus:ring-2 focus:ring-offset-2",
                    "hover:scale-105 active:scale-95",
                    "bg-gray-100 hover:bg-gray-200 text-gray-700",
                    "focus:ring-gray-300 focus:ring-offset-white",
                    "dark:bg-gray-800 dark:hover:bg-gray-700 dark:text-gray-300",
                    "dark:focus:ring-gray-600 dark:focus:ring-offset-gray-900"
                  )}
                  aria-label="Open login modal"
                  title="Login or sign up"
                >
                  <span className="text-xs font-medium">Login/Sign up</span>
                </button>
              )}
            </div>
          </nav>
        </div>

      </div>
    </header>
  );
};

export default Header;
